import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  extractColumnDefinitions,
  validateRowData,
  updateTableRow,
  ColumnFieldDefinition,
} from '@/services/tableRowService';
import { DynamicFormField } from './formFieldMapper';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import { useToast } from '@/hooks/use-toast';

interface EditTableRowModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableComponentId: string;
  rowId: string;
  rowData: Record<string, unknown>;
}

/**
 * Modal for editing existing rows in table components
 * Pre-populates form fields with existing row data
 */
export function EditTableRowModal({
  isOpen,
  onClose,
  tableComponentId,
  rowId,
  rowData,
}: EditTableRowModalProps) {
  // Try to get component from both editor and viewer stores
  const editorStore = useEditorStore();
  const viewerStore = useViewerStore();
  const { toast } = useToast();
  const [columns, setColumns] = useState<ColumnFieldDefinition[]>([]);
  const [tableName, setTableName] = useState<string>('Table');

  // Create form with react-hook-form
  const form = useForm<Record<string, unknown>>({
    defaultValues: rowData,
  });

  // Extract column definitions when modal opens or table changes
  useEffect(() => {
    if (isOpen && tableComponentId) {
      // Try editor store first, then viewer store
      let tableComponent = editorStore.getComponent?.(tableComponentId);
      if (!tableComponent) {
        tableComponent = viewerStore.getComponentById?.(tableComponentId);
      }

      console.log('✏️ [EditTableRowModal] Looking for table component:', {
        tableComponentId,
        rowId,
        foundInEditor: !!editorStore.getComponent?.(tableComponentId),
        foundInViewer: !!viewerStore.getComponentById?.(tableComponentId),
        tableComponent: tableComponent ? 'found' : 'not found',
      });

      if (tableComponent) {
        const columnDefs = extractColumnDefinitions(tableComponent);
        setColumns(columnDefs);

        // Set table name for display
        const name = (tableComponent.properties.title as string) || tableComponent.name || 'Table';
        setTableName(name);

        // Pre-populate form with existing row data
        const formData: Record<string, unknown> = {};
        columnDefs.forEach((col) => {
          // Use existing value or default
          if (rowData[col.name] !== undefined) {
            formData[col.name] = rowData[col.name];
          } else if (col.defaultValue !== undefined) {
            formData[col.name] = col.defaultValue;
          } else if (col.type === 'date') {
            formData[col.name] = new Date().toISOString().split('T')[0];
          } else if (col.type === 'select' && col.options && col.options.length > 0) {
            formData[col.name] = col.options[0].value;
          } else {
            formData[col.name] = '';
          }
        });

        form.reset(formData);
      } else {
        console.warn('✏️ [EditTableRowModal] Table component not found:', tableComponentId);
      }
    }
  }, [isOpen, tableComponentId, rowId, rowData, editorStore, viewerStore, form]);

  const onSubmit = (data: Record<string, unknown>) => {
    // Validate the data
    const validation = validateRowData(data, columns);
    if (!validation.valid) {
      toast({
        title: 'Validation Error',
        description: validation.errors.join(', '),
        variant: 'destructive',
      });
      return;
    }

    // Update the row in the table
    const success = updateTableRow(tableComponentId, rowId, data);

    if (success) {
      form.reset();
      onClose();

      // Show toast after closing to avoid React warning
      setTimeout(() => {
        toast({
          title: 'Success',
          description: 'Row updated successfully',
        });
      }, 100);
    } else {
      toast({
        title: 'Error',
        description: 'Failed to update row',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Row</DialogTitle>
          <DialogDescription>
            Update the entry in {tableName}. Modify the fields below.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 py-4 px-6">
              {columns.map((column) => (
                <DynamicFormField
                  key={column.name}
                  column={column}
                  control={form.control}
                  fieldName={column.name as never}
                />
              ))}
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Updating...' : 'Update Row'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
